// A code in an email or a text, ready to edit: only the code's own facts
// (what it's worth, what it covers, its dates and conditions) and its
// booking link, so nothing in the message is made up.
import moment from 'moment';
import { findTemplate } from '../../marketing-v2/features/email-composer/data/templates';
import { buildTemplateBlocks } from '../../marketing-v2/features/email-composer/email/apply';
import { SECTIONS } from '../../marketing-v2/features/email-composer/email/sections';
import { templateFor } from '../../marketing-v2/features/plans/data/emailFor';
import { coversSaid, daysSaid, headlineOf, money, offerWords, valueSaid } from '../data/codes';
import { linkFor } from '../links';
import { emailOfferFor } from './EmailCodeField';

let seq = 0;

// { blocks, subject }: the salon's header and footer, in their brand, with the
// code's Offer section between them (a template's other sections carry
// example prices and names, so they're left out)
export const codeEmail = (code, { market = 'hair', kit, links } = {}) => {
  const tpl = findTemplate(templateFor('fill', market), market);
  if (!tpl || !code) return { blocks: [], subject: '' };
  const built = buildTemplateBlocks(tpl, { market, kit, links });
  const w = offerWords(code);
  const offer = { ...emailOfferFor(code), eyebrow: w.eyebrow, button: 'Book with the code' };
  const out = [];
  let placed = false;
  const place = (b) => {
    if (placed) return;
    placed = true;
    out.push(b
      ? { ...b, content: { ...b.content, ...offer } }
      : { id: `s-offer-code-${++seq}`, type: 's-offer', content: { ...SECTIONS['s-offer'].defaults, ...offer } });
  };
  // The line inboxes show under the subject: the code's own facts, never
  // the template's example
  const preheader = `${headlineOf(code)} with ${code.code}${code.endsOn ? `. Book by ${moment(code.endsOn, 'YYYY-MM-DD').format('D MMM')}` : ''}.`;
  built.forEach((b) => {
    if (b.type === 's-header') out.push(b);
    else if (b.type === 's-offer') place(b);
    else if (b.type === 'footer') { place(null); out.push({ ...b, content: { ...b.content, preheader } }); }
  });
  place(null);
  return { blocks: out, subject: `${headlineOf(code)} with ${code.code}`, preheader };
};

// The conditions a text has room for, as one sentence: dates, who it's
// for, limits ("Book by 6 Nov, new clients only, one use each.")
export const textConditions = (code) => {
  const bits = [];
  if (code.endsOn) bits.push(`book by ${moment(code.endsOn, 'YYYY-MM-DD').format('D MMM')}`);
  const days = daysSaid(code.days);
  if (days) bits.push(`${days} only`);
  if (code.audience === 'new') bits.push('new clients only');
  if (code.minSpend) bits.push(`min spend ${money(code.minSpend)}`);
  if (Number(code.perClientCap) === 1) bits.push('one use each');
  const ch = code.channels || {};
  if (ch.online && !ch.salon) bits.push('online bookings only');
  if (!ch.online && ch.salon) bits.push('in person only');
  if (!bits.length) return '';
  const t = bits.join(', ');
  return `${t.charAt(0).toUpperCase()}${t.slice(1)}.`;
};

// A whole text: the offer, the code, its conditions and the link that applies
// it. The composer adds the opt-out line.
export const codeText = (code) => {
  const cond = textConditions(code);
  return `Hi {{FirstName}}, ${valueSaid(code)} ${coversSaid(code, { short: true })} with code ${code.code}.${cond ? ` ${cond}` : ''} ${linkFor(code, 'sms')}`;
};

// Just the code part, to drop into a text already being written
export const codeLine = (code) => {
  const cond = textConditions(code);
  return `Use ${code.code} for ${valueSaid(code)} ${coversSaid(code, { short: true })}.${cond ? ` ${cond}` : ''} ${linkFor(code, 'sms')}`;
};
